import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { Twitter, Linkedin, Instagram, Facebook, Heart } from "lucide-react";
import logoImage from "@assets/image_1757508460788.webp";
import { openCookieSettings } from "@/lib/consent";

export default function Footer() {
  const { t } = useTranslation();
  const handleRedirect = () => {
    window.open("https://www.realtyblocks.com", "_blank");
  };

  const socials = [
    { Icon: Twitter, label: "Twitter", testId: "social-twitter" },
    { Icon: Linkedin, label: "LinkedIn", testId: "social-linkedin" },
    { Icon: Instagram, label: "Instagram", testId: "social-instagram" },
    { Icon: Facebook, label: "Facebook", testId: "social-facebook" },
  ];

  return (
    <footer className="relative bg-card border-t border-border">
      <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-10">
          <div className="col-span-1 md:col-span-2">
            <button
              onClick={handleRedirect}
              className="flex items-center gap-3 mb-4 group"
              data-testid="footer-logo"
            >
              <img
                src={logoImage}
                alt="RealtyBlocks"
                className="w-9 h-9 object-contain transition-transform group-hover:scale-105"
              />
              <span className="text-xl font-display font-bold tracking-tight gradient-text">
                RealtyBlocks
              </span>
            </button>
            <p className="text-muted-foreground mb-6 max-w-md leading-relaxed">
              {t("footer.tagline")}
            </p>
            <div className="flex gap-2">
              {socials.map(({ Icon, label, testId }) => (
                <button
                  key={label}
                  onClick={handleRedirect}
                  aria-label={label}
                  className="w-10 h-10 rounded-lg bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors flex items-center justify-center"
                  data-testid={testId}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>

          <div>
            <h4 className="font-display font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">
              {t("footer.platform")}
            </h4>
            <ul className="space-y-2.5 text-muted-foreground text-sm">
              <li><a href="#" onClick={(e) => { e.preventDefault(); handleRedirect(); }} className="hover:text-foreground transition-colors" data-testid="link-for-buyers">{t("footer.forBuyers")}</a></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); handleRedirect(); }} className="hover:text-foreground transition-colors" data-testid="link-for-sellers">{t("footer.forSellers")}</a></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); handleRedirect(); }} className="hover:text-foreground transition-colors" data-testid="link-for-service-providers">{t("footer.forServiceProviders")}</a></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); handleRedirect(); }} className="hover:text-foreground transition-colors" data-testid="link-ai-tools">{t("footer.aiTools")}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="font-display font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">
              {t("footer.support")}
            </h4>
            <ul className="space-y-2.5 text-muted-foreground text-sm">
              <li><Link href="/how-it-works/methodology" className="hover:text-foreground transition-colors" data-testid="link-methodology">{t("footer.howWeCalculate")}</Link></li>
              <li><Link href="/history" className="hover:text-foreground transition-colors" data-testid="link-history">{t("footer.myValuations")}</Link></li>
              <li><a href="#" onClick={(e) => { e.preventDefault(); handleRedirect(); }} className="hover:text-foreground transition-colors" data-testid="link-help-center">{t("footer.helpCenter")}</a></li>
              <li><Link href="/contact" className="hover:text-foreground transition-colors" data-testid="link-contact-us">{t("footer.contactUs")}</Link></li>
              <li><Link href="/legal/privacy" className="hover:text-foreground transition-colors" data-testid="link-privacy-policy">{t("footer.privacyPolicy")}</Link></li>
              <li><Link href="/legal/terms" className="hover:text-foreground transition-colors" data-testid="link-terms-of-service">{t("footer.termsOfService")}</Link></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-border pt-6 mb-4">
          <h4 className="font-display font-semibold text-foreground mb-3 text-sm uppercase tracking-wider">Legal &amp; privacy</h4>
          <ul className="grid gap-y-2 gap-x-6 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 text-sm text-muted-foreground">
            <li><Link href="/legal/privacy" className="hover:text-foreground transition-colors" data-testid="footer-link-privacy">Privacy Policy</Link></li>
            <li><Link href="/legal/cookies" className="hover:text-foreground transition-colors" data-testid="footer-link-cookies">Cookie Policy</Link></li>
            <li><Link href="/legal/terms" className="hover:text-foreground transition-colors" data-testid="footer-link-terms">Terms of Service</Link></li>
            <li><Link href="/legal/disclaimer" className="hover:text-foreground transition-colors" data-testid="footer-link-disclaimer">Disclaimer</Link></li>
            <li><Link href="/legal/refund" className="hover:text-foreground transition-colors" data-testid="footer-link-refund">Refund &amp; Cancellation</Link></li>
            <li><Link href="/legal/grievance" className="hover:text-foreground transition-colors" data-testid="footer-link-grievance">Grievance Redressal</Link></li>
            <li><Link href="/legal/security" className="hover:text-foreground transition-colors" data-testid="footer-link-security">Security</Link></li>
            <li><Link href="/privacy/requests" className="hover:text-foreground transition-colors" data-testid="footer-link-dsr">Privacy / Data Requests</Link></li>
            <li><Link href="/privacy/contact" className="hover:text-foreground transition-colors" data-testid="footer-link-privacy-contact">Grievance / DPO Contact</Link></li>
            <li>
              <button
                type="button"
                onClick={() => openCookieSettings()}
                className="hover:text-foreground transition-colors text-left"
                data-testid="footer-link-cookie-settings"
              >
                Cookie settings
              </button>
            </li>
          </ul>
        </div>

        <div className="border-t border-border pt-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-muted-foreground text-sm">{t("footer.copyright")}</p>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <span>{t("footer.madeIn")}</span>
            <Heart className="h-3.5 w-3.5 text-accent fill-accent/30" />
          </div>
        </div>
        <p className="text-muted-foreground text-xs text-center mt-3">
          RealtyBlocks provides automated property estimates for informational purposes only — not a registered
          valuer&apos;s report. Verify RERA registration on your State portal before any transaction.
        </p>
      </div>
    </footer>
  );
}
